// Turns a refusal into a sentence in the reader's language.
//
// The server answers with a stable code and an English message. The code is
// what gets translated: first in the namespace the screen passes (a screen that
// invented a code knows best what it means), then in i18n/errors.ts, and only
// when neither knows it does the server's own English reach the page. A few
// refusals carry their meaning in a field rather than in the code — why a
// lesson is shut, until when an account is locked, which box a form got
// wrong — and are read from there.

import { ApiError } from '../api'
import { currentLocale, formatDate, translateIfAny } from '../i18n'
import type { Messages, Msg, Vars } from '../i18n'
import errors from '../i18n/errors'

type Say = (key: string, vars?: Vars) => string | undefined

function sayer<U extends Record<string, Msg>>(m?: Messages<U>): Say {
  return (key, vars) => (m ? translateIfAny(m, key, vars) : undefined) ?? translateIfAny(errors, key, vars)
}

/** Turns any thrown value into something worth showing a person. */
export function messageOf<U extends Record<string, Msg>>(err: unknown, m?: Messages<U>): string {
  const say = sayer(m)
  if (err instanceof ApiError) return apiMessage(err, say, m)
  if (err instanceof DOMException && err.name === 'AbortError') return say('err_aborted') ?? ''
  // A TypeError from fetch, an XHR that never answered: nothing arrived, so
  // there is nothing more precise to say than that.
  return say('err_network') ?? ''
}

/**
 * The translated complaint about one field of a 422, for a form that marks its
 * inputs one by one; undefined when that field is fine.
 */
export function fieldErrorOf<U extends Record<string, Msg>>(err: unknown, field: string, m?: Messages<U>): string | undefined {
  if (!(err instanceof ApiError)) return undefined
  const detail = err.fields[field]
  return detail === undefined ? undefined : fieldSentence(field, detail, sayer(m))
}

function apiMessage<U extends Record<string, Msg>>(err: ApiError, say: Say, m?: Messages<U>): string {
  const code = err.code

  // A screen's own word for its own code comes before any reading below.
  const own = m ? translateIfAny(m, `err_${code}`) : undefined
  if (own) return own

  const special = specialMessage(err, say)
  if (special) return special

  const known = say(`err_${code}`)
  if (known) return known

  // A code nobody translated yet: the server's own sentence is better than a
  // generic one, except where the client made the code up itself.
  if (code !== 'unknown' && code !== 'bad_response' && err.message) return err.message
  return say(statusKey(err.status)) ?? err.message
}

function specialMessage(err: ApiError, say: Say): string | undefined {
  const { code, fields } = err

  // An account locked by failed sign-ins says until when.
  if (code === 'locked' && fields['locked_until']) {
    return say('err_locked_until', { time: lockTime(fields['locked_until']) })
  }

  // A lesson that is shut says why: as a field from the lesson endpoint, or at
  // the end of the sentence from the progress endpoint.
  if (err.status === 403 && (code === 'forbidden' || code === 'locked')) {
    const reason = fields['blocked_reason'] ?? (code === 'locked' ? /:\s*(\w+)\s*$/.exec(err.message)?.[1] : undefined)
    if (reason) return say(`err_blocked_${reason}`) ?? say('err_blocked')
    if (code === 'locked') return say('err_blocked')
  }

  // The same code refuses a sign-in and a wrong current password; only the
  // second one is about a password the person is looking at.
  if (code === 'bad_credentials' && /current password/i.test(err.message)) {
    return say('err_wrong_current_password')
  }

  if (code === 'taken') {
    const field = Object.keys(fields)[0]
    return (field ? say(`err_taken_${field}`) : undefined) ?? say('err_taken')
  }

  if (code === 'too_large') {
    const limit = sizeLimit(err.message)
    if (limit) return say('err_too_large_limit', { limit })
  }

  if (code === 'invalid' || (err.status === 422 && Object.keys(fields).length > 0)) {
    const first = Object.entries(fields)[0]
    if (first) return fieldSentence(first[0], first[1], say)
  }
  return undefined
}

function statusKey(status: number): string {
  if (status === 0) return 'err_network'
  if (status === 401) return 'err_unauthorized'
  if (status === 403) return 'err_forbidden'
  if (status === 404) return 'err_not_found'
  if (status === 409) return 'err_conflict'
  if (status === 413) return 'err_too_large'
  if (status === 415) return 'err_unsupported_type'
  if (status === 422) return 'err_invalid'
  if (status === 423) return 'err_locked'
  if (status === 429) return 'err_rate_limited'
  if (status === 502 || status === 503 || status === 504) return 'err_unavailable'
  if (status >= 500) return 'err_internal'
  return 'err_unknown'
}

/** Today it is a time; on another day it needs the date as well. */
function lockTime(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  const sameDay = d.toDateString() === new Date().toDateString()
  return formatDate(
    iso,
    sameDay
      ? { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }
      : { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' },
  )
}

/** "an upload may be at most 200 MB" → "200 MB", written the local way. */
function sizeLimit(message: string): string | undefined {
  const m = /(\d+(?:\.\d+)?)\s*(KB|MB|GB)\b/i.exec(message)
  if (!m?.[1] || !m[2]) return undefined
  const unit = ({ KB: 'kilobyte', MB: 'megabyte', GB: 'gigabyte' } as const)[m[2].toUpperCase() as 'KB' | 'MB' | 'GB']
  try {
    return new Intl.NumberFormat(currentLocale(), { style: 'unit', unit }).format(Number(m[1]))
  } catch {
    return `${m[1]} ${m[2]}`
  }
}

// ------------------------------------------------------------------ fields --

/** The label key for an API field name: "payload.url" → field_url, "colors.dark.text" → field_colors. */
function labelKey(field: string): string {
  const name = field.replace(/^payload\./, '').replace(/^colors\..*$/, 'colors').replace(/\./g, '_')
  return `field_${name}`
}

/**
 * One field's complaint as a sentence. The server writes these in English as a
 * predicate on the field ("must not be empty"); the common shapes are
 * recognised and said properly, and anything else is named by its field.
 */
function fieldSentence(field: string, detail: string, say: Say): string {
  const label = say(labelKey(field))
  const numbers = (re: RegExp) => re.exec(detail)

  if ((field === 'phone' || field === 'phones') && !/required/i.test(detail)) return say('field_bad_phone') ?? detail
  if (/hex colou?r/i.test(detail)) return say('field_color') ?? detail
  if (/\bURL\b|https:\/\//i.test(detail)) return say('field_bad_url') ?? detail
  if (/not enrolled/i.test(detail)) return say('field_not_enrolled') ?? detail
  if (/already your password/i.test(detail)) return say('field_same_password') ?? detail

  const shape = numbers(/(\d+) to (\d+) characters/)
  if (shape && /letters, digits/i.test(detail)) return say('field_username_shape', { min: shape[1]!, max: shape[2]! }) ?? detail

  if (label) {
    const vars = { field: label, detail }
    if (shape) return say('field_length', { ...vars, min: shape[1]!, max: shape[2]! }) ?? detail
    const long = numbers(/(?:at most|longer than) (\d+) characters/)
    if (long) return say('field_too_long', { ...vars, max: long[1]! }) ?? detail
    const range = numbers(/between (\d+) and (\d+)/)
    if (range) return say('field_range', { ...vars, min: range[1]!, max: range[2]! }) ?? detail
    if (/\b(required|empty|cannot be null)\b|^enter your|^write something|at least one|^say why/i.test(detail)) {
      return say('field_required', vars) ?? detail
    }
    return say('field_invalid', vars) ?? detail
  }
  return say('err_invalid') ?? detail
}
